import ApplicationController from '../application_controller';
import Fetchers from 'core/fetchers';
import ValidationMessageGenerator from "./validity/message_generator";

export default class extends ApplicationController {
  static classes = [ 'hidden', 'success', 'error' ];
  static outlets = [ 'checkout--checkout', 'cart--order-summary', 'utilities--reveal' ];
  static targets = [ 'collapsed', 'expanded', 'couponCode', 'couponCodeWrapper', 'couponForm', 'success', 'successMessage', 'error', 'submitButton', 'label', 'labelText', 'labelIcon', 'couponTerms', 'freeShippingProgressBar' ];
  static values = { appliedCanvases: String };

  connect() {
    this.validationMessageGenerator = new ValidationMessageGenerator();
  }

  toggle() {
    const targetElements = [this.collapsedTarget, this.expandedTarget];
    const [elementToHide, elementToShow] =
      this.collapsed() ? targetElements : targetElements.reverse();
    elementToHide.classList.add(this.hiddenClass);
    elementToShow.classList.remove(this.hiddenClass);
  }

  collapsed() {
    return this.expandedTarget.classList.contains(this.hiddenClass);
  }

  submit() {
    const { value: couponCode } = this.couponCodeTarget;
    if (!couponCode) {
      this.errorTarget.innerText = 'You must enter a coupon code.';
      this.couponCodeWrapperTarget.classList.add(this.errorClass);
      this.submitButtonTarget.blur();
      return;
    }

    let baseUrl = `coupon_codes/validation/${couponCode}`;
    const params = new URLSearchParams();

    if (this.hasCheckoutCheckoutOutlet && this.checkoutCheckoutOutlet.hasFormTarget) {
      const shippingMethod = this.checkoutCheckoutOutlet.shippingMethodTarget;
      let billEmail = this.checkoutCheckoutOutlet.emailInputTarget?.value;

      if (billEmail && this.validationMessageGenerator.isValidEmailAddress(billEmail)) {
        params.append('bill_email', billEmail);
      }

      if (shippingMethod) {
        const shippingMethodInputs = shippingMethod.querySelectorAll('input[type="radio"]');
        const selectedShippingMethodId = shippingMethod.querySelector('input[type="radio"]:checked')?.value;
        
        // Pass all shipping method IDs
        const shippingMethodIds = Array.from(shippingMethodInputs).map(input => input.value);
        params.append('shipping_method_ids', shippingMethodIds.join(','));
        
        // Mark which one is selected
        if (selectedShippingMethodId) {
          params.append('selected_shipping_method_id', selectedShippingMethodId);
        }
      }
    }

    const url = `${baseUrl}?${params.toString()}`;

    Fetchers.fetchJSON(url, 'GET', null)
      .then((response) => response.json())
      .then((parsedResponse) => {
        if (parsedResponse.message === 'valid') {
          const { coupon_label_opts, coupon_terms_and_conditions } = parsedResponse;
          const { value: couponCode } = this.couponCodeTarget;
          this.couponCodeWrapperTarget.classList.remove(this.errorClass);
          this.couponFormTarget.classList.add(this.hiddenClass);
          this.successMessageTarget.innerText = couponCode;
          this.updateCouponLabel(coupon_label_opts);
          this.updateCouponTerms(coupon_terms_and_conditions)
          this.successTarget.classList.remove(this.hiddenClass);
          if (this.hasCheckoutCheckoutOutlet && this.checkoutCheckoutOutlet.hasFormTarget) {
            this.updateSidebar();
            this.checkoutCheckoutOutlet.refreshShippingMethodSection();
          }
          if (this.hasCartOrderSummaryOutlet) this.updateCartOrderSummary();
          this.updateAppliedCanvases(coupon_label_opts.coupon_discount_applied_canvases);
          if (this.hasUtilitiesRevealOutlet) this.toggleCqdMessage();
        } else {
          this.errorTarget.innerText = parsedResponse.message || parsedResponse.error[0];
          this.couponCodeWrapperTarget.classList.add(this.errorClass);
          this.submitButtonTarget.blur();
        }
      })
      .catch((err) => console.log(err));
  }

  clearCoupon() {
    Fetchers.fetchJSON('coupon_codes/deactivation', 'GET', null)
      .then((response) => {
        if (response.status < 400) {
          this.toggleFreeShippingBanner();
          this.successTarget.classList.add(this.hiddenClass);
          this.couponFormTarget.classList.remove(this.hiddenClass);
          if (this.hasCheckoutCheckoutOutlet && this.checkoutCheckoutOutlet.hasFormTarget) {
            this.updateSidebar();
            this.checkoutCheckoutOutlet.refreshShippingMethodSection();
          }
          if (this.hasCartOrderSummaryOutlet) this.updateCartOrderSummary();
          this.couponCodeTarget.value = '';
          this.toggle();
          this.utilitiesRevealOutlets.forEach(outlet => outlet.show());
        } else {
          console.log('Couldn\'t remove coupon.');
        }
      })
  }

  updateAppliedCanvases(couponDiscountAppliedCanvases) {
    if (!couponDiscountAppliedCanvases) return;

    this.appliedCanvasesValue = couponDiscountAppliedCanvases.canvases.join(', ');
    this.appliedProductDesignIdsValue = couponDiscountAppliedCanvases.product_design_ids.join(', ');
  }

  updateSidebar() {
    this.checkoutCheckoutOutlet.openLoadingOverlay();
    this.checkoutCheckoutOutlet.fetchData(
      this.checkoutCheckoutOutlet.urlValue,
      (data) => {
        this.checkoutCheckoutOutlet.updateSidebar(data);
        this.checkoutCheckoutOutlet.closeLoadingOverlay();
      },
      ['sidebar'],
      'PUT',
    );
  }

  updateCartOrderSummary() {
    this.cartOrderSummaryOutlet.updateOrderSummaryComponent();
  }

  updateCouponLabel(couponLabelOpts) {
    const { class_name: className, icon, text } = couponLabelOpts;

    this.labelTarget.classList = `m-order-summary__coupon__pill ${className}`
    this.labelTextTarget.innerText = text;
    this.labelIconTarget.querySelector('img').src = icon;
  }

  toggleCqdMessage() {
    if (this.appliedCanvasesValue?.length === 0 && this.appliedProductDesignIdsValue?.length === 0) return;

    this.utilitiesRevealOutlets.forEach((outlet) => {
      let { canvas, productDesignId } = outlet.element.dataset;

      if (this.appliedCanvasesValue.split(', ').includes(canvas) && this.appliedProductDesignIdsValue.split(', ').includes(productDesignId)) {
        outlet.hide();
      } else {
        outlet.show();
      }
    });
  }

  updateCouponTerms(couponTermsAndConditions) {
    const couponTerms = this.couponTermsTarget;

    couponTerms.innerHTML = '';

    couponTermsAndConditions.forEach((term) => {
      const termElement = document.createElement('li');
      termElement.innerText = term;
      couponTerms.appendChild(termElement);
    });
  }

  toggleFreeShippingBanner() {
    if (this.hasFreeShippingProgressBarTarget) {
      this.freeShippingProgressBarTarget.classList.remove(this.hiddenClass);
    }
  }
}
